import { type HTMLAttributes, forwardRef } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const variantStyles = {
  default: 'bg-white dark:bg-black',
  bordered:
    'bg-white dark:bg-black border border-gray-200 dark:border-gray-600',
  elevated: 'bg-white dark:bg-black shadow-md',
};

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-4 sm:p-6',
  lg: 'p-6 sm:p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'bordered', padding = 'md', className = '', children, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={`
        rounded-lg
        ${variantStyles[variant]}
        ${paddingStyles[padding]}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
});

// Card header component
export type CardHeaderProps = HTMLAttributes<HTMLDivElement>;

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  function CardHeader({ className = '', children, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={`mb-4 border-b border-gray-200 pb-4 dark:border-gray-600 ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// Card title component
export type CardTitleProps = HTMLAttributes<HTMLHeadingElement>;

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  function CardTitle({ className = '', children, ...props }, ref) {
    return (
      <h3
        ref={ref}
        className={`text-xl font-semibold ${className}`}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

// Card content component
export type CardContentProps = HTMLAttributes<HTMLDivElement>;

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  function CardContent({ className = '', children, ...props }, ref) {
    return (
      <div ref={ref} className={className} {...props}>
        {children}
      </div>
    );
  }
);

// Card footer component
export type CardFooterProps = HTMLAttributes<HTMLDivElement>;

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  function CardFooter({ className = '', children, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={`mt-4 border-t border-gray-200 pt-4 dark:border-gray-600 ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);
